import { FC, useEffect, useState } from 'react'

import { useNavigate } from '@tanstack/react-router'
import { KEY_CONNECTIONS_PATH, LICENSES_PATH } from 'commons/constants/routes'
import { License } from 'commons/types/DTO/bank/companyDetails/license'
import { TableDataQa } from 'commons/types/enums'
import { Layer, PageSectionPrimary, Table } from 'ui/components'
import { Button } from 'ui/components/Buttons'

import { handlerServerError } from '@orbit/errors'

import { CompanyLicensesTableProps } from './CompanyLicensesTableProps'
import { columns } from './columns'
import { useComponentEssence } from './hooks/useComponentsEssence'
import { useFilterCompanyLicensesWebPage } from './hooks/useFilterCompanyLicensesWebPage'

export const CompanyLicensesTable: FC<CompanyLicensesTableProps> = (props) => {
  const {
    data: { licenses, licensesTotalCount, isAPIConnectionColumnEnabled, id }
  } = props
  const navigate = useNavigate()
  const [webPageData, setWebPageData] = useState({
    licenses: licenses,
    licensesTotalCount: licensesTotalCount
  })

  useEffect(() => {
    setWebPageData({
      licenses: licenses,
      licensesTotalCount: licensesTotalCount
    })
  }, [licenses, licensesTotalCount])

  const { routine: filterCompanyLicensesWebPage } = useFilterCompanyLicensesWebPage({
    onSuccess: async (_, result) => {
      await setWebPageData(result)
    },
    onError: (error) => handlerServerError(error)
  })

  const { openDeleteConfirmation, DeleteConfirmation, loading, tableState, changePagination } = useComponentEssence(
    id,
    filterCompanyLicensesWebPage,
    webPageData.licensesTotalCount
  )

  const navigateToConnections = () => navigate({ to: KEY_CONNECTIONS_PATH, replace: true })

  return (
    <PageSectionPrimary
      title="Licenses"
      anchor="licenses"
      actions={
        <Button
          face="tertiary"
          onClick={() =>
            navigate({
              to: `${LICENSES_PATH}/add?returnUrl=${encodeURIComponent(
                window.location.pathname + window.location.hash
              )}&companyId=${id}`
            })
          }
        >
          Add New
        </Button>
      }
      face="outlined"
    >
      <DeleteConfirmation />
      <Layer rounded shadowed stretch>
        <Table<License>
          dataQa={TableDataQa.BP_COMPANY_LICENSES}
          columns={columns(openDeleteConfirmation, isAPIConnectionColumnEnabled, navigateToConnections)}
          dataSource={webPageData.licenses.map((el) => ({
            item: el
          }))}
          loading={loading}
          pagination={{
            page: tableState.page,
            size: tableState.size,
            onChange: changePagination,
            total: webPageData.licensesTotalCount
          }}
        />
      </Layer>
    </PageSectionPrimary>
  )
}
